/**
 * +castux: shared cast-bar cues for the enemy cast bars (nameplates, target frame, party frames),
 * which cannot take new constructor arguments everywhere (like game/prediction.ts localFeedback):
 *  - `castCues.kickReady`: the player's interrupt is off cooldown and usable, so interruptible
 *    enemy casts light up in the kick colour;
 *  - recent interrupts per unit ("Interrupted by Kaede" on the bar for a moment, party kicks too).
 * The HUD writes it once per frame / per event batch; FrameCastBar reads it.
 */
import type { AbilityDef, ClassDef, ContentRegistry, DeepReadonly, SimEvent, SimQueries, UnitId, World } from '@keystone/sim';
import { UI_BIND_KEYS, abilityForBind } from '../input/bindings';
import { abilityProcSpec } from './cluster/derive';

/** How long an enemy bar shows "Interrupted by …" (ms, real time). */
export const INTERRUPT_SHOW_MS = 1300;

export interface InterruptNote {
  /** performance.now() of the interrupt. */
  at: number;
  /** Who kicked ("You" for the player). */
  by: string;
  /** The interrupted spell's id. */
  abilityId: string;
}

export const castCues = {
  /** The player's interrupt is ready (an interruptible enemy cast shows the kick colour). */
  kickReady: false,
  /** Latest interrupt per interrupted unit. */
  interrupts: new Map<UnitId, InterruptNote>(),
};

/** The line on an interrupted bar. */
export function interruptedLabel(by: string | null): string {
  return by ? `Interrupted by ${by}` : 'Interrupted';
}

/** The class's bound interrupt ability (first hotbar key whose ability interrupts), if any. */
export function interruptAbilityOf(cls: Pick<ClassDef, 'binds' | 'extraAbilities' | 'extraBinds'> | undefined, ability: (id: string) => DeepReadonly<AbilityDef> | AbilityDef | undefined): string | null {
  if (!cls) return null;
  for (const bind of UI_BIND_KEYS) {
    const id = abilityForBind(cls, bind);
    const a = id ? ability(id) : undefined;
    if (a && abilityProcSpec(a as DeepReadonly<AbilityDef>).interrupt) return a.id;
  }
  return null;
}

/** Ready = alive, off cooldown and not school-locked (range is the player's call). */
export function kickReadyNow(world: DeepReadonly<World>, queries: Pick<SimQueries, 'cooldownRemaining'>, content: Pick<ContentRegistry, 'ability'>, playerId: UnitId | null, kickId: string | null): boolean {
  const u = playerId ? world.units[playerId] : undefined;
  if (!u?.alive || !kickId) return false;
  const a = content.ability(kickId);
  if (!a) return false;
  const lock = u.lockouts[a.school];
  if (lock !== undefined && lock > world.time) return false;
  try {
    return queries.cooldownRemaining(u.id, kickId) <= 0;
  } catch {
    return false;
  }
}

/** Note interrupts from a batch of events (who kicked, by name). */
export function noteInterrupts(events: readonly SimEvent[], world: DeepReadonly<World>, playerId: UnitId | null, realNow: number): void {
  for (const e of events) {
    if (e.type !== 'interrupt') continue;
    const by = e.sourceID === playerId ? 'You' : (world.units[e.sourceID]?.name ?? null);
    castCues.interrupts.set(e.targetID, { at: realNow, by: by ?? '', abilityId: e.extraAbilityGameID });
  }
}

/** The interrupt to show on `unitId`'s bar now, else null (expired notes are dropped). */
export function interruptShown(unitId: UnitId, realNow: number): InterruptNote | null {
  const n = castCues.interrupts.get(unitId);
  if (!n) return null;
  if (realNow - n.at > INTERRUPT_SHOW_MS || realNow < n.at) {
    castCues.interrupts.delete(unitId);
    return null;
  }
  return n;
}

export function resetCastCues(): void {
  castCues.kickReady = false;
  castCues.interrupts.clear();
}
